import { keepPreviousData } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';
import { formatDate } from 'date-fns';
import saveAs from 'file-saver';
import {
  ArchiveIcon,
  DownloadIcon,
  PlusIcon,
  PowerIcon,
  ScanBarcodeIcon,
  ShirtIcon,
} from 'lucide-react';

import { SkuDTO, SkuType } from '@invenco/products-interface';

import {
  AlertDialogWithAction,
  AlertDialogWithActionTrigger,
} from '@/components/alert-dialog-with-action';
import { Badge } from '@/components/core/badge';
import { Button } from '@/components/core/button';
import { DialogTrigger } from '@/components/core/dialog';
import { DataImporter } from '@/components/data-importer/data-importer';
import { TemplateKey } from '@/components/data-importer/types';
import {
  DataTable,
  DataTableFilterMenu,
  DataTablePageContent,
  DataTablePagination,
  DataTableRefreshMenu,
  DataTableToolbar,
  DataTableViewMenu,
  useDataTable,
  useDataTableState,
  useDataTableViews,
  useRowNavigationCallback,
  type View,
} from '@/components/data-table';
import {
  DataTableAction,
  DataTableActionMenu,
} from '@/components/data-table/data-table-action-menu';
import { FormDialog } from '@/components/forms/form-dialog';
import { Header, HeaderActions, HeaderIcon, HeaderTitle } from '@/components/header';
import { Page } from '@/components/layout';
import { DATA_POLLING_DURATION_MS } from '@/constants';
import { useGateways } from '@/gateways/GatewayProvider';
import { QuerySkusProps, SortField } from '@/gateways/impl/ProductsGateway';
import { formatNumber } from '@/shared/helpers';
import { useGatewayMutation, useQueryWithInput } from '@/shared/hooks/queries';
import { skuTypeTitle } from '@/shared/title-maps';

import { usePulseFlag } from '../../../../shared/hooks/use-pulse-flag';
import { useBatchedRequests } from '../../../../shared/hooks/useBatchedRequests';
import { SkuBadge } from '../../components/sku-badge';
import { CreateSkuDialogContent } from './create-sku-dialog-content';
import { ExportSkuDialogContent } from './export-sku-dialog-content';
import { SkuListItem, SkuListMapper } from './sku-list-mapper';

export const POLL_INTERVAL = 1000;

const isArchived = (sku: SkuListItem) => !!sku.archivedAt;
const isActive = (sku: SkuListItem) => !sku.archivedAt;

const columnHelper = createColumnHelper<SkuListItem>();

const columns = [
  columnHelper.accessor('name', {
    header: 'SKU / Description',
    cell: ({ getValue, row }) => (
      <div className="flex min-w-0 items-center gap-2">
        {/* TODO: Revisit whether SKU list rows should show SKU type icons. */}
        <SkuBadge name={getValue()} />
        {row.original.description ? (
          <span className="min-w-0 truncate text-sm">{row.original.description}</span>
        ) : null}
      </div>
    ),
    meta: { truncate: true },
  }),
  columnHelper.accessor('barcode', {
    header: 'Barcode',
  }),
  columnHelper.accessor('type', {
    header: 'Type',
    cell: ({ getValue }) => {
      const type = getValue();
      return type ? <Badge variant="secondary">{skuTypeTitle[type]}</Badge> : undefined;
    },
  }),
  columnHelper.display({
    id: 'dimensions',
    header: 'Dimensions',
    cell: ({ row }) => {
      const { length, width, height, dimensionUnit } = row.original;
      if (
        length === undefined ||
        width === undefined ||
        height === undefined ||
        dimensionUnit === undefined
      )
        return undefined;
      return `${formatNumber(length)} x ${formatNumber(width)} x ${formatNumber(height)} ${dimensionUnit.toLowerCase()}`;
    },
  }),
  columnHelper.display({
    id: 'weight',
    header: 'Weight',
    cell: ({ row }) => {
      const { weight, weightUnit } = row.original;
      if (weight === undefined || weightUnit === undefined) return undefined;
      return `${formatNumber(weight)} ${weightUnit.toLowerCase()}`;
    },
  }),
];

const presetViews: View[] = [
  {
    id: 'all',
    name: 'All',
    state: {
      filters: [
        {
          id: 'isArchived',
          operator: 'eq',
          value: false,
        },
      ],
    },
  },
  ...[SkuType.STANDARD, SkuType.BUNDLE, SkuType.MYSTERY, SkuType.ALIAS].map((type) => ({
    id: type,
    name: skuTypeTitle[type],
    state: {
      filters: [
        {
          id: 'isArchived',
          operator: 'eq',
          value: false,
        },
        {
          id: 'type',
          operator: 'in',
          value: [type],
        },
      ],
    },
  })),
  {
    id: 'archived',
    name: 'Archived',
    state: {
      filters: [{ id: 'isArchived', operator: 'eq', value: true }],
    },
  },
];

const tieBreakerSort = {
  key: 'name',
  direction: 'asc',
} as const;

export function SkuList() {
  const { productsGateway } = useGateways();
  const [isPolling, triggerPolling] = usePulseFlag(DATA_POLLING_DURATION_MS);
  const onClickRow = useRowNavigationCallback<SkuListItem>('/products/skus');

  const { run: archive, isRunning: isArchiving } = useBatchedRequests<SkuListItem, SkuDTO>({
    messageKey: 'sku-archive',
    progressMessage: ({ results, failures, total }) =>
      `Archived SKUs (${results.length}/${total}${
        failures.length ? ` - ${failures.length} failed` : ''
      })`,
  });

  const { run: restore, isRunning: isRestoring } = useBatchedRequests<SkuListItem, SkuDTO>({
    messageKey: 'sku-unarchive',
    progressMessage: ({ results, failures, total }) =>
      `Restored SKUs (${results.length}/${total}${
        failures.length ? ` - ${failures.length} failed` : ''
      })`,
  });

  const { mutate: exportAll } = useGatewayMutation({
    mutationFn: () => productsGateway.exportAllSkusToBlob(),
    onSuccess: (skuData) => {
      saveAs(skuData, `${formatDate(new Date(), 'yyyy-MM-dd')}_SKUs.csv`);
    },
  });

  const { views, isLoadingSavedViews, ...viewTabsProps } = useDataTableViews({
    tableId: 'skus',
    presetViews,
  });

  const { state, currentView, onChangeViewId, filters, ...tableProps } = useDataTableState({
    columns,
    filterDefs: {
      name: {
        variant: 'multiselect',
        label: 'SKU',
        icon: ScanBarcodeIcon,
        options: async (input, options) => {
          const result = await productsGateway.getSkus(
            { search: input.search, take: input.take },
            options,
          );
          return {
            items: result.items.map((sku) => sku.name),
          };
        },
      },
      type: {
        label: 'Type',
        variant: 'multiselect',
        icon: PowerIcon,
        options: [SkuType.STANDARD, SkuType.BUNDLE, SkuType.MYSTERY, SkuType.ALIAS],
        optionLabel: (option) => skuTypeTitle[option as SkuType] || option,
      },
      isArchived: {
        variant: 'boolean',
        label: 'Status',
        icon: ArchiveIcon,
        labels: {
          truthy: 'Archived',
          falsey: 'Active',
        },
      },
    },
    views,
    isLoadingSavedViews,
  });

  const filterMap = filters.getActiveFilterMap();
  const queryInput: QuerySkusProps = {
    take: state.pagination.pageSize,
    offset: state.pagination.pageSize * state.pagination.pageIndex,
    sortBy: [
      ...state.sorting.map((s) => ({
        key: s.id as SortField,
        direction: s.desc ? ('desc' as const) : ('asc' as const),
      })),
      tieBreakerSort,
    ],
    filters: {
      name: filterMap.name,
      isArchived: filterMap.isArchived,
      // TODO: Remove KIT mapping once backend sends BUNDLE instead of KIT
      type: filterMap.type
        ? {
            operator: filterMap.type.operator,
            value: filterMap.type.value.map((value) => (value === SkuType.BUNDLE ? 'KIT' : value)),
          }
        : undefined,
    },
    search: filters.search,
  };

  const { data, isLoading, isFetching, isPlaceholderData, isError, refetch } = useQueryWithInput({
    parentKey: 'skus',
    input: queryInput,
    query: (input, { signal }) => productsGateway.querySkus(input, { signal }),
    select: SkuListMapper.toData,
    // polling as there is a delay in changes in Lynk propagating to Hasura
    refetchInterval: isPolling ? POLL_INTERVAL : false,
    placeholderData: keepPreviousData,
  });

  const table = useDataTable({
    data: data?.data,
    rowCount: data?.total,
    columns,
    isLoading,
    isLoadingWithStaleData: isFetching && isPlaceholderData,
    hasError: isError,
    state,
    enableRowSelection: true,
    ...tableProps,
  });

  return (
    <Page>
      <Header>
        <HeaderIcon>
          <ShirtIcon />
        </HeaderIcon>
        <HeaderTitle>SKUs</HeaderTitle>
        <HeaderActions>
          <AlertDialogWithAction onAction={exportAll}>
            <AlertDialogWithActionTrigger asChild>
              <Button variant="outline">
                <DownloadIcon />
                Export
              </Button>
            </AlertDialogWithActionTrigger>
            <ExportSkuDialogContent />
          </AlertDialogWithAction>
          <DataImporter template={TemplateKey.SKUS} onComplete={triggerPolling} />
          <FormDialog>
            <DialogTrigger asChild>
              <Button>
                <PlusIcon />
                Create
              </Button>
            </DialogTrigger>
            <CreateSkuDialogContent />
          </FormDialog>
        </HeaderActions>
      </Header>

      <DataTablePageContent>
        <DataTableToolbar>
          <DataTableViewMenu
            views={views}
            currentView={currentView}
            filterState={filters.filterState}
            rowCount={data?.total}
            onChangeViewId={onChangeViewId}
            {...viewTabsProps}
          />
          <DataTableFilterMenu filters={filters} />
          <DataTableRefreshMenu onRefresh={refetch} />
        </DataTableToolbar>
        <DataTable table={table} onClickRow={onClickRow} />
        <DataTablePagination table={table} />
      </DataTablePageContent>
      <DataTableActionMenu table={table}>
        {(selectedRows) => {
          if (selectedRows.length > 0 && selectedRows.every(isActive)) {
            return (
              <DataTableAction
                disabled={isArchiving}
                onClick={async () => {
                  await archive({
                    execute: ({ id }) => productsGateway.archiveSku(id),
                    inputs: selectedRows,
                  });
                  triggerPolling();
                  table.resetRowSelection();
                }}
              >
                Archive
              </DataTableAction>
            );
          }

          if (selectedRows.length > 0 && selectedRows.every(isArchived)) {
            return (
              <DataTableAction
                disabled={isRestoring}
                onClick={async () => {
                  await restore({
                    execute: ({ id }) => productsGateway.unarchiveSku(id),
                    inputs: selectedRows,
                  });
                  triggerPolling();
                  table.resetRowSelection();
                }}
              >
                Restore
              </DataTableAction>
            );
          }

          return null;
        }}
      </DataTableActionMenu>
    </Page>
  );
}
